// Glass-i — Item Configuration Masters API client (Stage 6B.8A-2A).
//
// Three sibling resources under /api/erp/masters/item-config:
//   /categories
//   /material-types
//   /polish-types
//
// Each surface exports the same shape (list / lookup / get / create /
// update / updateStatus / remove) plus a per-resource grid-field map.

import client from '../../../../api/client';

const ROOT = '/api/erp/masters/item-config';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

function buildResource({ base, gridFieldMap }) {
  function mapField(camel) {
    if (!camel || typeof camel !== 'string') return camel;
    return gridFieldMap[camel] || camel;
  }
  function translateAdvancedRules(rules) {
    return rules.map((r) => {
      if (!r || typeof r !== 'object') return r;
      if (Array.isArray(r.rules)) return { ...r, rules: translateAdvancedRules(r.rules) };
      if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
      return r;
    });
  }
  function translateQuery(q) {
    if (!q || typeof q !== 'object') return q;
    const out = { ...q };
    if (Array.isArray(out.filters)) {
      out.filters = out.filters
        .filter((f) => f && f.field)
        .map((f) => ({ ...f, field: mapField(f.field) }));
    }
    if (Array.isArray(out.sort)) {
      out.sort = out.sort
        .filter((s) => s && s.field)
        .map((s) => ({ ...s, field: mapField(s.field) }));
    }
    if (typeof out.groupBy === 'string' && out.groupBy) {
      out.groupBy = mapField(out.groupBy);
    }
    if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
      out.advancedFilters = {
        ...out.advancedFilters,
        rules: translateAdvancedRules(out.advancedFilters.rules)
      };
    }
    return out;
  }
  return {
    list: (query) => client.post(`${base}/list`, translateQuery(query) || {}).then(unwrap),
    lookup: (params = {}) => client.get(`${base}/lookup`, { params }).then(unwrap)
      .then((d) => (d && Array.isArray(d.rows) ? d.rows : [])),
    get: (id) => client.get(`${base}/${encodeURIComponent(id)}`).then(unwrap),
    create: (payload) => client.post(base, payload).then(unwrap),
    update: (id, payload) => client.put(`${base}/${encodeURIComponent(id)}`, payload).then(unwrap),
    updateStatus: (id, body) =>
      client.patch(`${base}/${encodeURIComponent(id)}/status`, body).then(unwrap),
    remove: (id) => client.delete(`${base}/${encodeURIComponent(id)}`).then(unwrap)
  };
}

export const categoryApi = buildResource({
  base: `${ROOT}/categories`,
  gridFieldMap: {
    categoryCode:       'category_code',
    categoryName:       'category_name',
    parentCategoryId:   'parent_category_id',
    parentCategoryCode: 'parent_code',
    parentCategoryName: 'parent_name',
    categoryType:       'category_type',
    isSystem:           'is_system',
    isActive:           'is_active',
    sortOrder:          'sort_order',
    createdAt:          'created_at',
    updatedAt:          'updated_at'
  }
});

export const materialTypeApi = buildResource({
  base: `${ROOT}/material-types`,
  gridFieldMap: {
    materialTypeCode:    'material_type_code',
    materialTypeName:    'material_type_name',
    materialFamily:      'material_family',
    defaultCategoryId:   'default_category_id',
    defaultCategoryCode: 'default_category_code',
    defaultCategoryName: 'default_category_name',
    isGlass:             'is_glass',
    isProfile:           'is_profile',
    isHardware:          'is_hardware',
    isConsumable:        'is_consumable',
    isJobwork:           'is_jobwork',
    isSystem:            'is_system',
    isActive:            'is_active',
    sortOrder:           'sort_order',
    createdAt:           'created_at',
    updatedAt:           'updated_at'
  }
});

export const polishTypeApi = buildResource({
  base: `${ROOT}/polish-types`,
  gridFieldMap: {
    polishTypeCode: 'polish_type_code',
    polishTypeName: 'polish_type_name',
    isSystem:       'is_system',
    isActive:       'is_active',
    sortOrder:      'sort_order',
    createdAt:      'created_at',
    updatedAt:      'updated_at'
  }
});

export const brandApi = buildResource({
  base: `${ROOT}/brands`,
  gridFieldMap: {
    brandCode:    'brand_code',
    brandName:    'brand_name',
    displayName:  'display_name',
    countryCode:  'country_code',
    isSystem:     'is_system',
    isActive:     'is_active',
    sortOrder:    'sort_order',
    createdAt:    'created_at',
    updatedAt:    'updated_at'
  }
});

// Stage 6B.8A-3E — Brand ⇄ Supplier junction. Many-to-many, managed
// from the BrandModal "Suppliers" section. The replace endpoint is
// idempotent: send the desired set; server diffs and inserts /
// updates / deletes as needed.
export const brandSupplierApi = {
  list: (brandId) =>
    client.get(`${ROOT}/brands/${encodeURIComponent(brandId)}/suppliers`)
      .then(unwrap)
      .then((d) => (d && Array.isArray(d.rows) ? d.rows : [])),
  replace: (brandId, items) =>
    client.put(`${ROOT}/brands/${encodeURIComponent(brandId)}/suppliers`, { items })
      .then(unwrap)
      .then((d) => (d && Array.isArray(d.rows) ? d.rows : []))
};

// Read-only view from the supplier side: what brands does this
// supplier carry? Used by future Supplier-side UI; the endpoint exists
// now so the data is symmetric.
export const supplierBrandApi = {
  list: (supplierId) =>
    client.get(`${ROOT}/suppliers/${encodeURIComponent(supplierId)}/brands`)
      .then(unwrap)
      .then((d) => (d && Array.isArray(d.rows) ? d.rows : []))
};
